/* eslint-disable max-lines */
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useCameras, useLocations } from '@hakimo-ui/hakimo/data-access';
import { CameraGroupCamera } from '@hakimo-ui/hakimo/types';
import { useMemo, useState } from 'react';
import LocationCameraCard from './LocationCameraCard';
import SelectedCamerasCard from './SelectedCamerasCard';

interface Props {
  locationId: string;
  value: string[];
  currentGroupId?: string;
  onChange: (cameras: string[]) => void;
}

function CameraGroupSelectorCard({
  locationId,
  value,
  currentGroupId,
  onChange,
}: Props) {
  const { data: locations } = useLocations('');

  const locationName = useMemo(
    () => locations?.items.find((l) => String(l.id) === locationId)?.name,
    [locations, locationId]
  );

  const cameraSearchParams = locationName
    ? new URLSearchParams({
        locations: locationName,
        includeImage: 'true',
      }).toString()
    : '';

  const { data: camerasData, isLoading: isCamerasLoading } = useCameras(
    cameraSearchParams,
    !!locationName
  );

  const [search, setSearch] = useState('');
  const [dragCam, setDragCam] = useState<CameraGroupCamera | null>(null);

  const cameraMap = useMemo(
    () => new Map(camerasData?.items.map((c) => [c.id, c]) ?? []),
    [camerasData]
  );

  const { grouped, ungrouped } = useMemo(() => {
    if (!camerasData) {
      return { grouped: [], ungrouped: [] };
    }
    const selectedSet = new Set(value);
    const cameras = camerasData.items;
    const mapped = (c: (typeof cameras)[number]): CameraGroupCamera => ({
      id: c.id,
      name: c.name,
      locationId: c.location?.id ?? 0,
      isEnabled: true,
    });

    const groupedCameras = cameras
      .filter(
        (c) =>
          c.cameraGroupId &&
          c.cameraGroupId !== currentGroupId &&
          !selectedSet.has(c.id)
      )
      .map(mapped);

    const ungroupedCameras = cameras
      .filter(
        (c) =>
          !selectedSet.has(c.id) &&
          (!c.cameraGroupId || c.cameraGroupId === currentGroupId)
      )
      .map(mapped);

    return { grouped: groupedCameras, ungrouped: ungroupedCameras };
  }, [camerasData, value, currentGroupId]);

  const onDragStart = (event: DragStartEvent) => {
    setDragCam(event.active.data.current as CameraGroupCamera);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDragCam(null);

    if (!over || over.id !== 'selected-drop-zone') return;

    const cam = active.data.current as CameraGroupCamera;
    if (!cam || value.includes(cam.id)) return;

    onChange([...value, cam.id]);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const removeCamera = (cam: CameraGroupCamera) => {
    onChange(value.filter((id) => id !== cam.id));
  };

  const selectedCamera: CameraGroupCamera[] = useMemo(
    () =>
      value
        .map((id) => {
          const cam = cameraMap.get(id);
          if (!cam) return null;
          return {
            id: cam.id,
            name: cam.name,
            locationId: cam.location?.id ?? 0,
            isEnabled: true,
          };
        })
        .filter(Boolean) as CameraGroupCamera[],
    [value, cameraMap]
  );

  return (
    <DndContext
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      sensors={sensors}
    >
      <div className="grid grid-cols-2 gap-6">
        <LocationCameraCard
          locationId={locationId}
          search={search}
          setSearch={setSearch}
          dragCam={dragCam}
          cameraMap={cameraMap}
          ungroupedCameras={ungrouped}
          groupedCameras={grouped}
          isLoading={isCamerasLoading}
        />

        <SelectedCamerasCard
          cameras={selectedCamera}
          cameraMap={cameraMap}
          onRemove={removeCamera}
        />
      </div>

      <DragOverlay>
        {dragCam && (
          <div className="border-onlight-line-3 dark:border-ondark-line-2 bg-onlight-bg-3 dark:bg-ondark-bg-3/50 rounded-md border px-4 py-2 text-sm">
            {cameraMap.get(dragCam.id)?.name ?? dragCam.name}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

export default CameraGroupSelectorCard;
